import * as React from "react";

import Form, { FormComponentProps } from "@ant-design/compatible/es/form";
import { LockOutlined } from "@ant-design/icons";
import FormCard from "@impero-com/common/components/formCard";
import FormCardContainer from "@impero-com/common/components/formCardContainer";
import RightSideIcon from "@impero-com/common/components/rightSideIcon";
import passwordSvg from "@impero-com/common/img/password-login.svg";
import { AjaxManager } from "@impero-com/common/utils/ajax";
import { isUnauthenticated } from "@impero-com/common/utils/auth";
import withI18n, { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { OrgCookie } from "@impero-com/common/utils/orgCookie";
import { loginRedirectToRoot } from "@impero-com/common/utils/url";
import {
    Button,
    emitNotification,
    FormSubtitle,
    FormTitle,
} from "@impero-com/ui";
import { Input, Spin } from "antd";
import axios from "axios";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import PhoneInput from "react-phone-number-input";
import flags from "react-phone-number-input/flags";

import type { UserProfile } from "api/generated/model";
import { PostSetupData, SetupData } from "api/generated/model";
import {
    getSetup2fa,
    postSetup2fa,
    postValidatePin,
} from "api/generated/two-factor/two-factor";

import { DisplayRecoveryCode } from "./displayRecovery";
import { PinCodeForm } from "./pinCode";
import * as theme from "./theme.css";

import "react-phone-number-input/style.css";

const FormItem = Form.Item;

class Setup2FAForm extends React.Component<Setup2FAFormProps> {
    render() {
        const { form, phoneNumber, orgProfileCookie } = this.props;
        const { getFieldDecorator } = form;
        return (
            <>
                <FormTitle title={t("Setup2FA_Title")} />
                <FormSubtitle subtitle={t("Setup2FA_Description")} />
                <FormCard>
                    <FormItem className={theme.phoneInputContainer}>
                        {getFieldDecorator("phoneNumber", {
                            rules: [
                                {
                                    required: true,
                                    message: t("Setup2FA_PhoneNumberMissing"),
                                },
                            ],
                            initialValue: phoneNumber,
                        })(
                            <PhoneInput
                                className="ant-phone-number-input"
                                numberInputProps={{
                                    className: `ant-input ${theme.phoneInput}`,
                                }}
                                defaultCountry="DK"
                                displayInitialValueAsLocalNumber={true}
                                flags={flags}
                                onChange={() => {}}
                            />
                        )}
                    </FormItem>
                    {orgProfileCookie ? (
                        <FormItem>
                            {getFieldDecorator("password", {
                                rules: [
                                    {
                                        required: true,
                                        message: t("Login_PasswordRequired"),
                                    },
                                ],
                            })(
                                <Input
                                    prefix={
                                        <LockOutlined
                                            className={theme.iconOpacity}
                                        />
                                    }
                                    type="password"
                                    placeholder={t("Login_Password")}
                                />
                            )}
                        </FormItem>
                    ) : null}
                </FormCard>
            </>
        );
    }
}

interface Setup2FAFormProps extends FormComponentProps {
    phoneNumber?: string;
    verified: boolean;
    orgProfileCookie: UserProfile | null;
}

const WrappedSetup2FAForm = Form.create<Setup2FAFormProps>()(Setup2FAForm);

type RecoveryCodeContent =
    | { type: "NO_RECOVERY_CODE" }
    | { type: "RECOVERY_CODE"; recoveryCode: string };

class Setup2FA extends React.Component<Setup2FAProps, Setup2FAState> {
    formRef: Setup2FAForm | null = null;

    constructor(props: Setup2FAProps) {
        super(props);
        this.state = {
            status: { type: "DEFAULT" },
            setupData: undefined,
            verifying: false,
            wrongPin: false,
            requiresHumanVerification: false,
        };
    }

    componentDidMount() {
        this.renewSetupData();
    }

    renewSetupData = () => {
        const { ajaxManager } = this.props;
        return ajaxManager
            .ajax<SetupData, Setup2FAStatus>(getSetup2fa, {
                component: this,
                initialStatusValue: "DEFAULT",
                getErrorStatus: () => ({ type: "DEFAULT" }),
                getSuccessStatus: () => ({ type: "DEFAULT" }),
                inFlightStatus: { type: "FETCHING_DATA" },
            })
            .then((setupData) => this.setState({ setupData }));
    };

    saveFormRef = (formRef: Setup2FAForm | null) => {
        this.formRef = formRef;
    };

    render() {
        const {
            status,
            setupData,
            verifying,
            wrongPin,
            requiresHumanVerification,
        } = this.state;
        if (!setupData) {
            return <Spin />;
        }
        const { loggedIn, orgCookie, orgProfileCookie } = this.props;
        switch (status.type) {
            case "DISPLAY_RECOVERY": {
                return (
                    <DisplayRecoveryCode
                        recoveryCode={status.recoveryCode}
                        orgCookie={orgCookie}
                    />
                );
            }
            default: {
                return (
                    <FormCardContainer
                        fullWidth={loggedIn ? true : false}
                        rightSide={
                            loggedIn ? undefined : (
                                <RightSideIcon
                                    icon={passwordSvg}
                                    customImages={true}
                                    orgCookie={orgCookie}
                                />
                            )
                        }
                        showLogo={!loggedIn}
                    >
                        <WrappedSetup2FAForm
                            wrappedComponentRef={this.saveFormRef}
                            phoneNumber={
                                setupData.phoneNumber
                                    ? "+" +
                                      setupData.phoneNumber.prefix +
                                      setupData.phoneNumber.number
                                    : undefined
                            }
                            verified={setupData.verified}
                            orgProfileCookie={orgProfileCookie}
                        />
                        {verifying ? (
                            <PinCodeForm
                                onSubmitPin={this.handleSubmitPin}
                                submitButtonText={t("Login_ConfirmButton")}
                                wrongPin={wrongPin}
                                requiresHumanVerification={
                                    requiresHumanVerification
                                }
                                challenge={this.props.challenge}
                            />
                        ) : (
                            <>
                                <span className={theme.setup2FaButton}>
                                    <Button
                                        id="use-number"
                                        variant="primary"
                                        onClick={this.handleSubmit}
                                        block
                                    >
                                        {t("Setup2FA_Button")}
                                    </Button>
                                </span>
                            </>
                        )}
                    </FormCardContainer>
                );
            }
        }
    }

    handleSubmit = () => {
        if (!this.formRef) {
            return;
        }
        const { form } = this.formRef.props;
        form.validateFields((err: any, values: any) => {
            if (err) {
                return;
            }

            const {
                phoneNumber,
                password,
            }: { phoneNumber: string; password: string } = values;
            const parsed = parsePhoneNumberFromString(phoneNumber);
            if (parsed === undefined) {
                return;
            }

            const submission: PostSetupData = {
                prefix: parsed.countryCallingCode,
                number: parsed.nationalNumber,
                password,
                // `requiresHumanVerification` in a rejected response is not handled here
                // Should not be necessary since we're setting up our 2FA (as opposed to updating it)
                captcha: null,
                challenge: this.props.challenge ?? null,
            };

            const { ajaxManager } = this.props;
            ajaxManager
                .ajax<SetupData, Setup2FAStatus>(
                    () => postSetup2fa(submission),
                    {
                        component: this,
                        initialStatusValue: "DEFAULT",
                        getSuccessStatus: () => ({ type: "DEFAULT" }),
                        getErrorStatus: () => ({ type: "DEFAULT" }),
                        inFlightStatus: { type: "SUBMITTING" },
                        expectedStatus: [200],
                        onErrorCode: {
                            401: (e) => {
                                const data = e.response?.data;
                                if (
                                    !isUnauthenticated(data) ||
                                    data.status !== "notAuthenticated"
                                ) {
                                    throw new Error(t("Login_UnknownAuthType"));
                                }
                                form.setFields({
                                    password: {
                                        value: "",
                                        errors: [
                                            new Error(
                                                t(
                                                    "Login_UsernameOrPasswordIncorrect"
                                                )
                                            ),
                                        ],
                                    },
                                });
                            },
                        },
                    }
                )
                .then((data) => {
                    if (data.verified) {
                        // If the phone number is verified, we won't display a recovery code
                        this.handlePinValidated({ type: "NO_RECOVERY_CODE" });
                    } else {
                        this.setState({
                            verifying: true,
                        });
                    }
                });
        });
    };

    // TODO: Migrate to the AJAX manager
    handleSubmitPin = (pin: string, captcha: string | null) => {
        // Avoid double-sending the query
        if (this.state.status.type !== "DEFAULT") {
            return;
        }

        this.setState({
            wrongPin: false,
            status: { type: "VALIDATING_PIN" },
        });

        postValidatePin({
            pin,
            challenge: this.props.challenge,
            human_verification_response: captcha,
        })
            .then((res) => {
                const { recoveryCode } = res.data;
                const content: RecoveryCodeContent =
                    recoveryCode !== null && recoveryCode !== undefined
                        ? { type: "RECOVERY_CODE", recoveryCode }
                        : { type: "NO_RECOVERY_CODE" };
                this.handlePinValidated(content);
            })
            .catch((err: unknown) => {
                const data = axios.isAxiosError(err)
                    ? err.response?.data
                    : undefined;
                this.setState((state) => ({
                    wrongPin: true,
                    requiresHumanVerification:
                        state.requiresHumanVerification ||
                        (isUnauthenticated(data) &&
                            data.status === "notAuthenticated" &&
                            data.requiresHumanVerification),
                    status: { type: "DEFAULT" },
                }));
            });
    };

    handlePinValidated = (recoveryCode: RecoveryCodeContent) => {
        this.setState({ status: { type: "DEFAULT" } });
        emitNotification("success", t("Setup2FA_UpdateSuccess"));

        switch (recoveryCode.type) {
            case "NO_RECOVERY_CODE":
                if (this.props.loggedIn) {
                    setTimeout(loginRedirectToRoot, 2000);
                } else {
                    this.renewSetupData();
                }
                break;
            case "RECOVERY_CODE":
                this.setState({
                    status: {
                        type: "DISPLAY_RECOVERY",
                        recoveryCode: recoveryCode.recoveryCode,
                    },
                });
                break;
            default:
                assertNever(recoveryCode);
        }
    };
}

interface Setup2FAProps {
    loggedIn: boolean;
    ajaxManager: AjaxManager;
    challenge: string | undefined;
    orgCookie: OrgCookie;
    orgProfileCookie: UserProfile | null;
}

type Setup2FAStatus =
    | { type: "DEFAULT" }
    | { type: "FETCHING_DATA" }
    | { type: "SUBMITTING" }
    | { type: "VALIDATING_PIN" }
    | { type: "DISPLAY_RECOVERY"; recoveryCode: string };

interface Setup2FAState {
    status: Setup2FAStatus;
    setupData: SetupData | undefined;
    wrongPin: boolean;
    requiresHumanVerification: boolean;
    verifying: boolean;
}

const LocalizedSetup2FA = withI18n<Setup2FAProps>(Setup2FA);

export { LocalizedSetup2FA as Setup2FA, PinCodeForm };
